import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    CardContent,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

const TableFieldEditor = ({ field, onUpdate }) => {
    const [expanded, setExpanded] = useState(false);

    const columnTypes = [
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'select', label: 'Dropdown' },
        { value: 'date', label: 'Date' },
        { value: 'time', label: 'Time' },
        { value: 'datetime', label: 'Date & Time' },
        { value: 'checkbox', label: 'Checkbox' }
    ];

    const addColumn = () => {
        const newColumn = {
            id: `col_${Date.now()}`,
            label: 'New Column',
            type: 'text',
            required: false,
            options: []
        };

        const updatedColumns = [...(field.columns || []), newColumn];
        onUpdate({ columns: updatedColumns });
    };

    const updateColumn = (columnIndex, updates) => {
        const updatedColumns = [...(field.columns || [])];
        updatedColumns[columnIndex] = { ...updatedColumns[columnIndex], ...updates };
        onUpdate({ columns: updatedColumns });
    };

    const removeColumn = (columnIndex) => {
        const updatedColumns = field.columns.filter((_, index) => index !== columnIndex);
        onUpdate({ columns: updatedColumns });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                            Table Configuration
                        </Typography>
                        <Chip 
                            label={`${field.columns?.length || 0} columns`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Configure the columns for this table input
                            </Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addColumn}
                                variant="outlined"
                                size="small"
                            >
                                Add Column
                            </Button>
                        </Box>

                        {field.columns && field.columns.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {field.columns.map((column, index) => (
                                    <Card key={column.id} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2">
                                                        Column {index + 1}
                                                    </Typography>
                                                    <Chip 
                                                        label={column.type}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeColumn(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Column Label"
                                                        value={column.label}
                                                        onChange={(e) => updateColumn(index, { label: e.target.value })}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Column Type</InputLabel>
                                                        <Select
                                                            value={column.type}
                                                            onChange={(e) => updateColumn(index, { type: e.target.value })}
                                                            label="Column Type"
                                                        >
                                                            {columnTypes.map((type) => (
                                                                <MenuItem key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={column.required || false}
                                                                onChange={(e) => updateColumn(index, { required: e.target.checked })}
                                                                size="small"
                                                            />
                                                        }
                                                        label="Required"
                                                    />
                                                </Grid>

                                                {column.type === 'text' && (
                                                    <Grid item xs={12} md={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Placeholder"
                                                            value={column.placeholder || ''}
                                                            onChange={(e) => updateColumn(index, { placeholder: e.target.value })}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                )}

                                                {column.type === 'number' && (
                                                    <>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Min Value"
                                                                type="number"
                                                                value={column.min || ''}
                                                                onChange={(e) => updateColumn(index, { min: e.target.value })}
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Max Value"
                                                                type="number"
                                                                value={column.max || ''}
                                                                onChange={(e) => updateColumn(index, { max: e.target.value })}
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Step"
                                                                type="number"
                                                                value={column.step || ''}
                                                                onChange={(e) => updateColumn(index, { step: e.target.value })}
                                                                size="small"
                                                                placeholder="1"
                                                            />
                                                        </Grid>
                                                    </>
                                                )}

                                                {column.type === 'select' && (
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            label="Options (one per line)"
                                                            multiline
                                                            rows={3}
                                                            value={column.options?.join('\n') || ''}
                                                            onChange={(e) => updateColumn(index, { 
                                                                options: e.target.value.split('\n').filter(opt => opt.trim()) 
                                                            })}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.stopPropagation();
                                                                }
                                                            }}
                                                            size="small"
                                                            helperText="Enter each option on a new line"
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={(theme) => ({
                                p: 3,
                                textAlign: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                            })}>
                                <Typography color="text.secondary" gutterBottom>
                                    No columns configured
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Add columns to define the table structure
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addColumn}
                                    variant="contained"
                                    size="small"
                                >
                                    Add First Column
                                </Button>
                            </Box>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default TableFieldEditor;
